import { differenceInSeconds, isValid, parseISO } from "date-fns";
import { toZonedTime, format } from "date-fns-tz";
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "react-bootstrap";
import classNames from "classnames";
import { WorkflowMode } from "../models/Workflow";
import { BdaService, BdaServiceApplicationProperty, BdaServiceProperty, ServiceCategory } from "../models/BdaService";
import { ExecutionStatus } from "../models/generics";
import { LinkProps } from "react-router-dom";
import { AgentGenerationBody } from "../api/Agents";
import { RcFile } from "antd/es/upload";
import { NavigationSection, SectionKey } from "../components/layout/AppSidebar";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { User } from "../models/User";
import { Group } from "../models/ControlPanel";
import { DataNode } from "antd/es/tree";
import { DatabaseDatasource, Datasource, FileSystemDatasource, MessageBrokerDatasource, ObjectStorageDatasource, RemoteDatasource } from "../models/Datasource";

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export type PortInfo = {
  id: string;
  type?: string;
  nodeId?: string;
  nodeName?: string;
  acceptedTypes?: string[];
  datasetType?: string;
  datasetMode?: string;
  description?: string;
  generic?: boolean;
  http?: boolean;
}

interface AssetInfo {
  name: string;
  path: string;
  type: 'dataset' | 'model';
  description: string;
}
interface ServiceAssets {
  inputs: AssetInfo[];
  outputs: AssetInfo[];
}

// interface HttpPortInfo {
//   port: string;
//   name: string;
//   http_model: string;
//   url: string;
//   auth?: any;
// }
// // interface ServiceHttpPorts {
// //   inputs: HttpPortInfo[];
// //   outputs: HttpPortInfo[];
// // }

export function isValidDatasource(datasource: Datasource) {
  let validDatasourceFields: boolean = false;
  if (datasource.type === 'database' && setObjectType<DatabaseDatasource>(datasource)) {
    validDatasourceFields = !!datasource.database && !!datasource.host;
  }
  else if (datasource.type === 'fileSystem' && setObjectType<FileSystemDatasource>(datasource)) {
    validDatasourceFields = !!datasource.path && !!datasource.filename && !!datasource.extension;
  }
  else if (datasource.type === 'messageBroker' && setObjectType<MessageBrokerDatasource>(datasource)) {
    validDatasourceFields = !!datasource.brokers;
  }
  else if (datasource.type === 'objectStorage' && setObjectType<ObjectStorageDatasource>(datasource)) {
    validDatasourceFields = !!datasource.host && !!datasource.bucket && !!datasource.accessKey && !!datasource.secretKey;
  }
  else if (datasource.type === 'remote' && setObjectType<RemoteDatasource>(datasource)) {
    validDatasourceFields = !!datasource.url;
  }
  return !!datasource.driverType && validDatasourceFields;
}

export const renderTopicLink = (topic: string) => {
  const url = window.__env.KAKFA_UI_TOPIC_MESSAGES_URL.replace("$TOPIC_NAME", topic);
  return (
    <a className="d-flex align-items-center" href={url} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon icon={solid('comments')} className="me-1" />
      <span className="me-2">{topic}</span>
    </a>
  )
}

export const getBase64EncodedImage = (file: RcFile): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export type CustomBootstrapLinkType = React.ComponentClass<LinkProps> & keyof JSX.IntrinsicElements;

export function setObjectType<T>(object: any): object is T { return object !== undefined }

function getNodePortsPerTypes(properties: BdaServiceProperty[], portType: 'input' | 'output', assetType: "dataset" | "model") {
  const nodePorts = properties
    .filter(prop => prop.key.startsWith(`${portType}-${assetType}`))
    .map(prop => {
      const portInfo: PortInfo = { id: prop.key, type: assetType };
      if (assetType === "dataset") {
        portInfo.datasetType = prop.extra?.datasetType;
        portInfo.datasetMode = prop.extra?.mode || "batch";
        if (prop.extra?.format === "endpoint") portInfo.http = true;

        if (portType === "input" && !prop.extra?.format) {
          const columnsKey = prop.key.replace("dataset", "columns");
          portInfo.acceptedTypes = properties.find(prop => prop.key === columnsKey)?.defaultValue?.split(",") || [];
        }
      }
      return portInfo;
    });

  const assetsProperty = properties.find(prop => prop.key === 'assets');
  if (assetsProperty) {
    let assetsInfo: ServiceAssets | undefined;
    try {
      assetsInfo = JSON.parse(assetsProperty.defaultValue);
    } catch (err) {
      console.log(err);
    }
    if (assetsInfo?.[`${portType}s`]) {
      const assetsPorts: PortInfo[] = assetsInfo[`${portType}s`]
        .filter(info => info.type === assetType)
        .map(info => ({ id: info.name, type: assetType, generic: true, description: info.description }));
      nodePorts.push(...assetsPorts);
    }
  }

  return nodePorts;
}

// function getNodeHttpPorts(properties: BdaServiceProperty[], portType: 'input' | 'output') {
//   const nodePorts: PortInfo[] = [];
//   const httpPortsProperty = properties.find(prop => prop.key === 'httpPorts');
//   if (httpPortsProperty) {
//     let httpPortsInfo: ServiceHttpPorts | undefined;
//     try {
//       httpPortsInfo = JSON.parse(httpPortsProperty.defaultValue);
//     } catch (err) {
//       console.log(err);
//     }
//     if (httpPortsInfo?.[`${portType}s`]) {
//       const assetsPorts: PortInfo[] = httpPortsInfo[`${portType}s`]
//         .map(info => ({ id: info.name, type: 'http', ...info })); // TODO add description to port JSON
//       nodePorts.push(...assetsPorts);
//     }
//   }
//   return nodePorts;
// }

export function serviceHasProperty(service: BdaService, key: string) {
  return service.properties.some(prop => prop.key === key);
}

export function getNodePortsFromProperties(properties: BdaServiceProperty[]) {
  const applicationProperties = properties.filter(prop => prop.type === 'application') as BdaServiceApplicationProperty[];

  // TODO improve the function (should call it just once - at max twice?)
  const inputDatasets = getNodePortsPerTypes(applicationProperties, 'input', 'dataset');
  const inputModels = getNodePortsPerTypes(applicationProperties, 'input', 'model');

  const outputDatasets = getNodePortsPerTypes(applicationProperties, 'output', 'dataset');
  const outputModels = getNodePortsPerTypes(applicationProperties, 'output', 'model');

  // httpPorts are now among the application properties (field of "input-dataset" prop)
  // const staticProperties = properties.filter(prop => prop.type === 'static') as BdaServiceStaticProperty[];
  // const inputHttpPorts = getNodeHttpPorts(staticProperties, 'input');
  // const outputHttpPorts = getNodeHttpPorts(staticProperties, 'output');

  return {
    inputs: [...inputDatasets, ...inputModels],
    outputs: [...outputDatasets, ...outputModels]
  };
}

export function isValidDate(date: string) {
  const parsed = parseISO(date);
  return isValid(parsed);
}

const formatInTimeZone = (date: Date, fmt: string, tz: string) => format(toZonedTime(date, tz), fmt, { timeZone: tz });

export function formatDate(date: string, format?: string) { return formatInTimeZone(parseISO(date + "Z"), format || 'dd/MM/yyyy HH:mm', currentTimeZone) }

export const modesOptions = [
  { label: 'Batch', value: WorkflowMode.BATCH, icon: solid('cog') },
  { label: 'Stream', value: WorkflowMode.STREAM, icon: solid('stream') },
];

export const pageSizeOptions = [
  { label: "20 elements", value: 20 },
  { label: "40 elements", value: 40 },
  { label: "80 elements", value: 80 },
  { label: "100 elements", value: 100 },
];

export const accessLevelOptions = [
  { label: "Private", value: "PRIVATE", icon: solid('user-secret') },
  { label: "Team", value: "TEAM", icon: solid('users') },
  { label: "Public", value: "PUBLIC", icon: solid('globe-africa') },
];

export const datasourceOptions = [
  { key: 'objectStorage', label: 'Object Storage', value: 'objectStorage', icon: solid('memory') },
  { key: 'messageBroker', label: 'Message Broker', value: 'messageBroker', icon: solid('comments') },
  { key: 'remote', label: 'Remote Source', value: 'remote', icon: solid('globe') },
  { key: 'database', label: 'Tabular Storage', value: 'database', icon: solid('database') },
  { key: 'fileSystem', label: 'FileSystem Storage', value: 'fileSystem', icon: solid('folder-tree') },
];

export const removePrefixPathFromAssetLocation = (location: string, prefixPath: string) => {
  return location.replace(prefixPath, "");
}

export const getStatusIcon = (status: ExecutionStatus, noClass?: boolean) => {
  var icon, className = 'text-dark', spin = false;

  switch (status) {
    case ExecutionStatus.RUNNING:
      icon = solid('cog');
      spin = true;
      break;
    case ExecutionStatus.STOPPING:
      icon = solid('ban');
      spin = true;
      break;
    case ExecutionStatus.STOPPED:
      icon = solid('stop');
      break;
    default:
      icon = regular('clock');
      break;
    case ExecutionStatus.STARTING:
      icon = solid('spinner');
      spin = true;
      break;
    case ExecutionStatus.ERROR:
      icon = solid('exclamation-circle');
      className = 'text-danger';
      break;
    case ExecutionStatus.COMPLETED:
      icon = regular('check-circle');
      className = 'text-success';
      break;
  }

  return <FontAwesomeIcon icon={icon} spin={spin} className={classNames({ [className]: !noClass })} />
}

export const getStatusBadge = (status: ExecutionStatus) => {
  return (
    <Badge bg="none" className={`service-badge ${status}`}>
      {getStatusIcon(status, true)}
      <span className="ms-1">{status}</span>
    </Badge>
  );
}

export function getAgentGenerationPayload(data: any[]): AgentGenerationBody {
  return {
    redisKey: window.__env.GENAI_AGENT_REDIS_KEY,
    redisNamespace: window.__env.GENAI_AGENT_REDIS_NAMESPACE,
    details: {
      id: "123",
      message: {
        role: "user",
        parts: [{ text: generateCsvFromPreview(data) }],
      }
    }
  };
}

export const getStringType = (value: string): "string" | "json" => {
  try {
    const parsed = JSON.parse(value);
    const isValidType = typeof parsed === 'object' && parsed !== null;
    return isValidType ? "json" : "string";
  } catch {
    return "string";
  }
};

const generateCsvFromPreview = (data: any[]) => {
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row: any) => headers.map(field => row[field as keyof typeof row]).join(","))
  ];
  return csvRows.join("\n");
}

export const fixProcedure = (procedure: any[], services: BdaService[]) => {
  return procedure.map(element => {
    // TODO remove batch and stream cases
    if (element.type !== 'service' && element.type !== 'batch' && element.type !== 'stream') return element;

    const service = services.find(s => s.ref === element.id);

    return {
      ...element,
      data: {
        ...element.data,
        statusModel: service?.statusModel
      }
    };
  });
}

export const getPropertyIcon = (type: BdaServiceProperty['type']) => {
  switch (type) {
    case 'application':
    default:
      return regular('play-circle');
    case 'resource': return solid('pie-chart');
    case 'tuning': return solid('wrench');
    case 'static': return regular('hdd');
  }
}

export const getSelectedColumns = (service: BdaService, inputDatasetKey?: string) => {
  return inputDatasetKey
    ? service.properties.find(prop => prop.key === inputDatasetKey.replace('dataset', 'columns'))?.value.split(",")
    : service.properties.find(prop => prop.key === "input-columns")?.value.split(",");
}

export const getExecutionTime = (startTime: string, endTime: string) => {
  const durationInSeconds = differenceInSeconds(new Date(endTime), new Date(startTime));

  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  let formatted = `${seconds}s`;
  if (minutes > 0) formatted = `${minutes}m ` + formatted;
  if (hours > 0) formatted = `${hours}h ` + formatted;

  return formatted;
};


export function normalizePath(original: string) {
  let normalized = original.replace(/(\/)+/g, "/");
  if (normalized.at(-1) !== "/") normalized += "/";
  return normalized;
}

export const fixDirName = (name: string) => name.replace(/\//g, "");

export const notificationTypesOptions = [
  { label: "Application Status Changed", value: "BDA_APP_STATUS_CHANGE" },
  { label: "Application Asset Created", value: "BDA_APP_ASSET_CREATED" },
  // { label: "Application Information Edited", value: "BDA_APP_EDITED" },
];

export const SMART_UPDATE_EXAMPLE_PAYLOAD = {
  services: [
    {
      serviceLabel: "service-example1",
      propertyKey: "propertyKey1",
      value: "0.7"
    },
    {
      serviceLabel: "service-example2",
      propertyKey: "propertyKey2",
      value: "0.5"
    },
  ]
};

export const TARGET_EXAMPLE_PAYLOAD = {
  "runtimeClassName": null,
  "affinity": {
    "nodeAffinity": {
      "requiredDuringSchedulingIgnoredDuringExecution": {
        "nodeSelectorTerms": [
          {
            "matchExpressions": [
              {
                "key": "key1",
                "operator": "In",
                "values": [
                  "value1"
                ]
              }
            ]
          }
        ]
      }
    }
  },
  "env": {
    "key1": "value1",
    "key2": "value2"
  },
  "tolerations": [
    {
      "key": "key1",
      "operator": "Exists/Equal",
      "value": "value1",
      "effect": "NoExecute/NoSchedule/PreferNoSchedule"
    }
  ]
}

export const sectionIconsMap: Record<SectionKey, IconDefinition> = {
  'dashboard': solid('gauge'),
  'data-management': solid('magnifying-glass-chart'),
  'datasources': solid('faucet-drip'),
  'datasets': solid('database'),
  'models': solid('graduation-cap'),
  'assets-management': solid('shapes'),
  'services': solid('cubes'),
  'applications': solid('laptop-code'),
  'projects': solid('folder'),
  'llm': solid('robot'),
  'rags': solid('brain'),
  'loaders': solid('bars-progress'),
  'llm-models': solid('microchip'),
  'api-keys': solid('key'),
  'environment-variables': solid('sliders'),
  'control-panel': solid('server'),
  'dataspace-connectors': solid('plug'),
}

type SectionEntry = [key: string, section: NavigationSection];
export const mapSections = ([key, section]: SectionEntry, navigationSections: Record<SectionKey, NavigationSection>) => ({
  key,
  icon: sectionIconsMap[key as SectionKey],
  title: section.labels.title,
  url: section.url,
  order: section.order,
  hide: section.hide,
  admin: section.admin,
  items: section.items?.map(sectionKey => ({
    key: sectionKey,
    icon: sectionIconsMap[sectionKey],
    title: navigationSections[sectionKey].labels.title,
    url: navigationSections[sectionKey].url,
    hide: navigationSections[sectionKey].hide,
    admin: navigationSections[sectionKey].admin,
  })),
});

export function serviceDefinitionNeedsCleaning(service: BdaService) {
  if (
    service.id !== undefined || service.ref !== undefined || service.label !== undefined || service.statusModel !== undefined ||
    service.models !== undefined || service.organization !== undefined || service.owner !== undefined ||
    service.properties?.some(prop => prop.id !== undefined)
  ) return true;
  return false;
}

export function mapCategoriesToFlatList(categories: ServiceCategory[]) {
  const flatList: Array<{ id: number, pId?: number, value: string | number, title: string }> = [];
  categories.forEach(c => {
    flatList.push(({ id: c.id!, title: c.name, value: c.id! }));
    if (c.children && c.children.length > 0) {
      c.children.forEach(sub => flatList.push(({ id: sub.id!, title: sub.name, value: sub.id!, pId: c.id })));
    }
  })
  return flatList;
}

export function unescapeDelimiter(value?: string) {
  if (!value) return;
  const map: Record<string, string> = {
    '\\t': '\t',
    '\\n': '\n',
    '\\r': '\r',
    '\\s': ' ',
    '\\\\': '\\'
  };
  return value.replace(/\\[tnrs\\]/g, match => map[match] || match);
};

export function convertKiBtoGiB(kib: string) {
  return (parseInt(kib) / (1024 ** 2)).toFixed(2) + ' GB';
}

export const renderConditionStatus = (condition: any, ready?: boolean) => {
  if (!["True", "False"].includes(condition.status)) return condition.status;
  const icon = ready ? condition.status === "True" ? solid("circle") : solid("circle-xmark") : condition.status === "True" ? solid("circle-xmark") : solid("circle");
  return <FontAwesomeIcon icon={icon} className={classNames("ms-1", { "text-success": icon === solid("circle"), "text-danger": icon === solid("circle-xmark") })} />;
};

export const isAdmin = (user?: User, permissions?: Group[]) => (user?.groups || permissions?.map(p => p.path) || []).some(group => group === (window.__env.ADMIN_GROUP_PATH || '/Permissions/Admin'));

export const findInTree = (nodes: any[], value: number): any | undefined => {
  for (const node of nodes) {
    if (node.value === value) return node;

    if (node.children) {
      const found = findInTree(node.children, value);
      if (found) return found;
    }
  }

  return undefined;
}

export const buildTree = (nodes: ServiceCategory[]): DataNode[] => {
  return nodes.map(node => ({
    key: node.id!,
    title: node.name,
    value: node.id!,
    isLeaf: !node.children?.length,
    children: node.children ? buildTree(node.children) : undefined,
  }));
};